<template>
  <div class="flex flex-col gap-6">
    <div v-if="loading">
      <titlePrimary class="backoffice-title w-full text-lg mt-4" :title="$t('requestDetailAdmin.titles.request')" />
    </div>
    <div v-else-if="error" class="text-red-500 text-center p-4">
      {{ error }}
    </div>
    <div v-else class="backoffice-request-header">
      <div class="flex items-center justify-between gap-4">
        <titlePrimary class="backoffice-title w-full text-lg" :title="$t('requestDetailAdmin.titles.request') + ' ' + requestData.code" />
        <!-- Histórico interno de trazabilidad: solo Alexandra y Paola. -->
        <button
          v-if="canViewHistory"
          @click="openHistoryModal"
          class="shrink-0 p-2 rounded-full border border-gray-300 bg-white hover:bg-gray-100 transition-colors duration-200"
          :title="$t('requestHistory.open')"
          :aria-label="$t('requestHistory.open')"
        >
          <HistoryIcon class="w-5 h-5 text-[#385660] hover:text-[#2AB4D9]" />
        </button>
      </div>
      <div v-if="canEditRequest" class="backoffice-request-actions flex flex-wrap gap-2 px-2 sm:px-4">
        <ButtonWhite @click="handleClickChangeStatus" class="!w-auto !px-4 !py-2 !shadow-sm text-base">
          {{ $t('requestDetailAdmin.actions.changeStatus') }}
          <ChangeIcon class="h-5 w-5" />
        </ButtonWhite>
        <ButtonWhite @click="handleClickChange" class="!w-auto !px-4 !py-2 !shadow-sm text-base">
          {{ $t('requestDetailAdmin.actions.changeType') }}
          <ChangeIcon class="h-5 w-5" />
        </ButtonWhite>
        <ButtonWhite @click="handleClickEdit" class="!w-auto !px-4 !py-2 !shadow-sm text-base">
          {{ $t('requestDetailAdmin.actions.edit') }}
          <EditIcon class="h-5 w-5" />
        </ButtonWhite>
      </div>
    </div>
  </div>

  <div class="py-4 sm:py-6 px-2 sm:px-4 bg flex flex-col gap-10 text-[#7CADB9] overflow-hidden">
    <!-- Sin responsable solo se puede consultar y asignar. -->
    <div v-if="isUnassignedRequest"
      class="rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800">
      {{ $t('requestDetail.locked.unassigned') }}
    </div>

    <!-- La solicitud pertenece a otro gestor: se puede consultar, no modificar. -->
    <div v-if="isAssignedToAnotherManager"
      class="rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800">
      {{ $t('requestDetail.locked.assignedToAnotherManager') }}
    </div>

    <!-- DATOS DE LA SOLICITUD -->
    <div class="backoffice-card flex flex-col justify-between gap-x-2 p-3 sm:p-4 md:flex-row">
      <div class="flex flex-col justify-end gap-1 py-2 flex-1">
        <span class="font-semibold text-black">Estado de la solicitud</span>
        <span class="text-[#7CADB9] text-lg font-semibold border border-gray-300 rounded-md p-2">{{ $t(`common.requestStatus.${requestData?.status}`) || requestData?.status || '-' }}</span>
      </div>
      <div class="flex flex-col justify-end gap-1 py-2 flex-1">
        <span class="font-semibold text-black">Administrador de fincas / API / Inmobiliaria</span>
        <div class="flex items-center border border-gray-300 rounded-md px-3">
          <span class="flex-1 text-[#7CADB9] text-lg font-semibold py-2">{{ clientAccountName }}</span>
          <button v-if="role == 'superadmin' && canEditRequest" @click="openManagerSearchModal"
            class="ml-2 p-1 hover:bg-gray-100 rounded transition-colors duration-200"
            :title="$t('requestDetailAdmin.actions.editManager') || 'Editar Administrador'">
            <EditIcon class="w-4 h-4 text-[#385660] hover:text-[#2AB4D9]" />
          </button>
        </div>
      </div>
      <div class="flex flex-col justify-end gap-1 py-2 flex-1">
        <span class="font-semibold text-black">{{ $t('requestDetailAdmin.labels.accountType') }}</span>
        <span class="text-[#7CADB9] text-lg font-semibold border border-gray-300 rounded-md p-2">{{ clientAccountTypeLabel }}</span>
      </div>
      <div class="flex flex-col justify-end gap-1 py-2 flex-1">
        <span class="font-semibold text-black">{{ $t('requestDetailAdmin.labels.typology') }}</span>
        <span class="text-[#7CADB9] text-lg font-semibold border border-gray-300 rounded-md p-2">{{ typeLabel }}</span>
      </div>
      <div class="flex flex-col justify-end gap-1 py-2 flex-1">
        <span class="font-semibold text-black">Persona asignada</span>
        <div class="flex items-center border border-gray-300 rounded-md px-3">
          <span class="flex-1 text-[#7CADB9] text-lg font-semibold py-2">{{ requestData?.assigned_to?.name ?? '-' }}</span>
          <button v-if="canAssignRequest" @click="openUserSearchModal"
            class="ml-2 p-1 hover:bg-gray-100 rounded transition-colors duration-200"
            :title="$t('requestDetailAdmin.actions.editAssigned')">
            <EditIcon class="w-4 h-4 text-[#385660] hover:text-[#2AB4D9]" />
          </button>
        </div>
      </div>
    </div>

    <!-- Primera fila: dos cajas en la misma fila -->
    <div class="flex flex-col lg:flex-row gap-4">
      <!-- Columna de datos: cliente y suministro -->
      <div class="flex flex-col w-full lg:w-1/2 gap-0 flex-1 min-w-0">
        <!-- DATOS DEL TITULAR -->
        <div class="pb-2">
          <titlePrimary class="backoffice-title mb-1" :title="$t('requestDetailAdmin.titles.clientData')" />
        </div>
        <div class="backoffice-card p-3 sm:p-4">
          <div class="grid grid-cols-1 md:grid-cols-2 gap-x-2 gap-y-1">

            <div v-if="loading" class="col-span-full text-center p-4">
              {{ $t('requestDetail.loading.propertyData') }}
            </div>

            <div v-else-if="error" class="col-span-full text-red-500 text-center p-4">
              {{ error }}
            </div>
            <template v-else-if="requestData">
              <!-- Nombre, apellidos, DNI y teléfono los mantiene ADS desde aquí,
                   en cualquier tipología. -->
              <HolderDataForm
                :request-data="requestData"
                :disabled="requestData?.can_edit_holder_data !== true || isRequestLockedForWriting"
                @saved="handleHolderDataSaved"
              />
              <div v-if="requestData.contact_person" class="flex flex-col gap-1 py-2">
                <span class="font-semibold text-black">{{ $t('requestDetailAdmin.labels.contact_person') }}</span>
                <span class="text-[#7CADB9] text-lg border border-gray-300 rounded-md p-2">{{ requestData.contact_person }}</span>
              </div>
              <div v-if="visible.email" class="flex flex-col gap-1 py-2">
                <span class="font-semibold text-black">{{ $t('requestDetailAdmin.labels.clientEmail') }}</span>
                <span class="text-[#7CADB9] text-lg border border-gray-300 rounded-md p-2">{{ requestData.email ?? '-' }}</span>
              </div>

              <div v-if="visible.iban" class="flex flex-col gap-1 py-2 md:col-span-2">
                <span class="font-semibold text-black">{{ $t('requestDetailAdmin.labels.iban') }}</span>
                <span class="text-[#7CADB9] text-lg border border-gray-300 rounded-md p-2 break-all">{{ requestData.iban ?? '-' }}</span>
              </div>

              <div v-if="visible.user_address" class="flex flex-col gap-1 py-2 md:col-span-2">
                <span class="font-semibold text-black truncate">{{ $t('requestDetailAdmin.labels.correspondenceAddress') }}</span>
                <span class="text-[#7CADB9] text-lg border border-gray-300 rounded-md p-2 break-words">{{ requestData.user_address ?? '-' }}</span>
              </div>
              <div v-if="visible.user_address" class="flex flex-col gap-1 py-2 md:col-span-2">
                <span class="font-semibold text-black truncate">{{ $t('requestDetailAdmin.labels.correspondencePopulation') }}</span>
                <span class="text-[#7CADB9] text-lg border border-gray-300 rounded-md p-2 break-words">{{ requestData.correspondence_population ?? '-' }}</span>
              </div>
            </template>

          </div>
        </div>
        <!-- DATOS DEL SUMINISTRO -->
        <div class="pt-4 pb-2">
          <titlePrimary class="backoffice-title mb-1" :title="$t('requestDetailAdmin.titles.supplyData')" />
        </div>
        <div class="backoffice-card p-3 sm:p-4">
        <!-- <div class="p-3 sm:p-4 rounded-xl border shadow-[0px_4px_4px_0px_rgba(0,0,0,0.25)] overflow-y-auto"> -->
          <div class="grid grid-cols-1 md:grid-cols-2 gap-x-2 gap-y-1">
            <!-- DATOS DEL SUMINISTRO -->
            <div v-if="loading" class="text-center p-4">
              {{ $t('requestDetail.loading.propertyData') }}
            </div>
            <div v-else-if="error" class="text-red-500 text-center p-4">
              {{ error }}
            </div>
            <!-- <div v-else-if="requestData">
              <InputPrimary type="text" :label="$t('requestDetailAdmin.labels.cupsLight')"
                :placeholder="$t('requestDetailAdmin.placeholders.cupsLight')" :modelValue="requestData.cups_light ?? '-'"
                placeholderColor="#7CADB9" textColor="#7CADB9" labelColor="#000000"  />
              <InputPrimary type="text" :label="$t('requestDetailAdmin.labels.cupsGas')"
                :placeholder="$t('requestDetailAdmin.placeholders.cupsGas')" :modelValue="requestData.cups_gas ?? '-'"
                placeholderColor="#7CADB9" textColor="#7CADB9" labelColor="#000000"  />
              <InputPrimary class="md:col-span-2" type="text" :label="$t('requestDetailAdmin.labels.supplyPointAddress')"
                :placeholder="$t('requestDetailAdmin.placeholders.supplyPointAddress')"
                :modelValue="requestData.supply_address ?? '-'" placeholderColor="#7CADB9" textColor="#7CADB9"
                labelColor="#000000"  />
              <InputPrimary type="text" :label="$t('requestDetailAdmin.labels.postalCode')"
                :placeholder="$t('requestDetailAdmin.placeholders.postalCode')" :modelValue="requestData.postal_code ?? '-'"
                placeholderColor="#7CADB9" textColor="#7CADB9" labelColor="#000000"  />
              <InputPrimary type="text" :label="$t('requestDetailAdmin.labels.municipality')"
                :placeholder="$t('requestDetailAdmin.placeholders.municipality')" :modelValue="requestData.municipality ?? '-'"
                placeholderColor="#7CADB9" textColor="#7CADB9" labelColor="#000000"  />
            </div> -->
            <template v-else-if="requestData">
              <div v-if="visible.cups" class="flex flex-col gap-1 py-2">
                <span class="font-semibold text-black">{{ $t('requestDetailAdmin.labels.cupsLight') }}</span>
                <span class="text-[#7CADB9] text-lg border border-gray-300 rounded-md p-2 break-all">{{ cupsLightValue || '-' }}</span>
              </div>
              <div v-if="visible.cups" class="flex flex-col gap-1 py-2">
                <span class="font-semibold text-black">{{ $t('requestDetailAdmin.labels.cupsGas') }}</span>
                <span class="text-[#7CADB9] text-lg border border-gray-300 rounded-md p-2 break-all">{{ cupsGasValue || '-' }}</span>
              </div>

              <div v-if="visible.meter_readings || requestData.light_meter_reading" class="flex flex-col gap-1 py-2">
                <span class="font-semibold text-black">{{ $t('requestDetailAdmin.labels.lightMeterReading') }}</span>
                <span class="text-[#7CADB9] text-lg border border-gray-300 rounded-md p-2">{{ requestData.light_meter_reading || '-' }}</span>
              </div>
              <div v-if="visible.meter_readings || requestData.gas_meter_reading" class="flex flex-col gap-1 py-2">
                <span class="font-semibold text-black">{{ $t('requestDetailAdmin.labels.gasMeterReading') }}</span>
                <span class="text-[#7CADB9] text-lg border border-gray-300 rounded-md p-2">{{ requestData.gas_meter_reading || '-' }}</span>
              </div>

              <div v-if="visible.supply_address" class="flex flex-col gap-1 py-2 md:col-span-2">
                <span class="font-semibold text-black">{{ $t('requestDetailAdmin.labels.supplyPointAddress') }}</span>
                <span class="text-[#7CADB9] text-lg border border-gray-300 rounded-md p-2 break-words">{{ requestSupplyAddress }}</span>
              </div>
              <div v-if="visible.postal" class="flex flex-col gap-1 py-2">
                <span class="font-semibold text-black">{{ $t('requestDetailAdmin.labels.postalCode') }}</span>
                <span class="text-[#7CADB9] text-lg border border-gray-300 rounded-md p-2">{{ requestData.postal_code ?? '-' }}</span>
              </div>
              <div v-if="visible.municipality" class="flex flex-col gap-1 py-2">
                <span class="font-semibold text-black">{{ $t('requestDetailAdmin.labels.municipality') }}</span>
                <span class="text-[#7CADB9] text-lg border border-gray-300 rounded-md p-2">{{ requestData.municipality ?? '-' }}</span>
              </div>

              <div v-if="requestData.includes_water" class="flex items-center gap-2 py-2 md:col-span-2">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-[#2AB4D9] shrink-0" viewBox="0 0 20 20" fill="currentColor">
                  <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 11.586 7.707 10.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
                </svg>
                <span class="font-semibold text-[#385660]">{{ $t('form.labels.includes_water') }}</span>
              </div>

              <!-- Solicitudes relacionadas por CUPS Luz -->
              <div class="flex flex-col gap-1 py-2 md:col-span-2">
                <span class="font-semibold text-black">{{ $t('requestDetailAdmin.labels.relatedRequests') || 'Solicitudes con el mismo CUPS Luz' }}</span>
                  <select class="w-full border border-gray-300 rounded-md px-3 py-2 text-sm text-[#7CADB9]" :disabled="!relatedRequests.length" @change="e => navigateToRequest(e.target.value)">
                    <option value="" disabled selected>Selecciona una solicitud</option>
                    <option v-for="req in relatedRequests" :key="req.id" :value="req.id">
                      {{ req.code }} - {{ $t(`common.requestStatus.${req.status}`) }}
                    </option>
                  </select>
                <div v-if="!relatedRequests.length && cupsLightValue" class="text-xs text-gray-400 italic py-2 mt-2">
                  No hay otras solicitudes con este CUPS Luz.
                </div>
                <div v-else-if="!cupsLightValue" class="text-xs text-gray-400 italic py-2 mt-2">
                  Sin CUPS Luz para buscar solicitudes relacionadas.
                </div>
              </div>
              <!-- Solicitudes relacionadas por CUPS Gas -->
              <div class="flex flex-col gap-1 py-2 md:col-span-2">
                <span class="font-semibold text-black">{{ $t('requestDetailAdmin.labels.relatedGasRequests') || 'Solicitudes con el mismo CUPS Gas' }}</span>
                  <select class="w-full border border-gray-300 rounded-md px-3 py-2 text-sm text-[#7CADB9]" :disabled="!relatedGasRequests.length" @change="e => navigateToRequest(e.target.value)">
                    <option value="" disabled selected>Selecciona una solicitud</option>
                    <option v-for="req in relatedGasRequests" :key="req.id" :value="req.id">
                      {{ req.code }} - {{ $t(`common.requestStatus.${req.status}`) }}
                    </option>
                  </select>
                <div v-if="!relatedGasRequests.length && cupsGasValue" class="text-xs text-gray-400 italic py-2 mt-2">
                  No hay otras solicitudes con este CUPS Gas.
                </div>
                <div v-else-if="!cupsGasValue" class="text-xs text-gray-400 italic py-2 mt-2">
                  Sin CUPS Gas para buscar solicitudes relacionadas.
                </div>
              </div>
            </template>

          </div>
        </div>

        <!-- NOTAS INTERNAS -->
        <div class="flex flex-col w-full min-w-0 gap-0 pt-4 lg:grow">
          <div class="pb-2 mx-0">
            <titlePrimary class="backoffice-title mb-1" :title="$t('requestDetailAdmin.titles.internalNotes')" />
          </div>
          <div v-if="loading" class="text-center p-4">
            {{ $t('requestDetail.loading.propertyData') }}
          </div>
          <div v-else-if="error" class="text-red-500 text-center p-4">
            {{ error }}
          </div>
          <div v-else-if="requestData" class="h-[clamp(22rem,40vh,30rem)] min-h-0 min-w-0 lg:h-auto lg:min-h-[clamp(22rem,40vh,30rem)] lg:flex-1">
            <Notes
              :message="requestData.internal_notes || []"
              :request_id="requestData.id.toString()"
              :sentToId="requestData.manager?.id ?? null"
              :isNotes="true"
              :isInternal="true"
              :isReadOnly="isRequestLockedForWriting"
              :matchNotificationStyle="true"
              class="backoffice-card backoffice-chat-card h-full"
            />
          </div>
        </div>
        <ContractImprovementBlock
          v-if="requestData && (supportsContractImprovement(requestData.type) || requestData.contract_improvement_accepted_at)"
          class="backoffice-card backoffice-contract-improvement"
          :requestData="requestData"
          readOnly
          :canManageWorkflow="canEditRequest"
          @saved="fetchrequestDetailAdmin"
        />
      </div>
      <!-- Columna de chat + Hoja de ruta dividida -->
      <div class="flex flex-col w-full lg:w-1/2 gap-2 min-w-0">
        <div class="rounded-xl flex flex-col gap-0 p-0 sm:p-0 min-w-0 lg:grow">
          <!-- Título principal: Notificaciones -->
          <div class="pb-2 mx-0">
            <titlePrimary class="backoffice-title mb-1" :title="$t('requestDetailAdmin.titles.notifications')" />
          </div>

          <!-- Notificaciones -->
          <div class="pb-0 min-h-0 lg:flex lg:flex-col lg:grow">
            <div v-if="loading" class="text-center p-4">
              {{ $t('requestDetail.loading.propertyData') }}
            </div>
            <div v-else-if="error" class="text-red-500 text-center p-4">
              {{ error }}
            </div>
            <div v-else-if="requestData" class="h-[clamp(22rem,40vh,30rem)] min-h-0 min-w-0 lg:h-auto lg:min-h-[clamp(22rem,40vh,30rem)] lg:flex-1">
              <Notes :message="requestData.notifications" :request_id="requestData.id.toString()"
                :sentToId="requestData.manager?.id ?? null" :isReadOnly="isRequestLockedForWriting"
                class="backoffice-card backoffice-chat-card h-full" />
            </div>
          </div>

          <!-- Hoja de ruta -->
          <div class="pt-2 min-h-0 lg:flex lg:flex-col lg:grow">
            <div class="pb-2 mx-0">
              <titlePrimary class="backoffice-title mb-1" :title="$t('requestDetailAdmin.titles.roadmap')" />
            </div>

            <div v-if="loading" class="text-center p-4">
              {{ $t('requestDetail.loading.propertyData') }}
            </div>
            <div v-else-if="error" class="text-red-500 text-center p-4">
              {{ error }}
            </div>
            <div v-else-if="requestData" class="h-[clamp(22rem,40vh,30rem)] min-h-0 min-w-0 lg:h-auto lg:min-h-[clamp(22rem,40vh,30rem)] lg:flex-1">
              <Notes :message="requestData.notes" :request_id="requestData.id.toString()" :sentToId="requestData.manager?.id ?? null"
                :isNotes="true" :showCalendar="true" :isReadOnly="isRequestLockedForWriting"
                :matchNotificationStyle="true" class="backoffice-card backoffice-chat-card h-full" />
            </div>
          </div>

          <div v-if="requestData?.can_view_operational_details === true" class="pt-5">
            <div class="pb-2 mx-0">
              <titlePrimary class="backoffice-title mb-1" :title="$t('requestDetailAdmin.internalOperation.title')" />
            </div>

            <div class="backoffice-card space-y-3 p-3 sm:p-4">
              <fieldset
                v-for="detail in liquidationDetails"
                :key="detail.supply_type"
                :disabled="!canEditLiquidation || isSavingLiquidation"
                :data-supply-type="detail.supply_type"
                class="flex min-w-0 flex-col gap-3 border-b border-gray-100 pb-3 last:border-b-0 last:pb-0 2xl:flex-row 2xl:items-center"
              >
                <div class="shrink-0 font-semibold text-[#385660] 2xl:w-24 flex items-center justify-center text-center">
                  {{ detail.supply_type === 'light' ? $t('requestDetailAdmin.internalOperation.cupsLight') : $t('requestDetailAdmin.internalOperation.cupsGas') }}
                </div>

                <div class="flex min-w-0 flex-1 flex-col gap-3 sm:flex-row sm:flex-wrap">
                  <label class="flex min-w-0 flex-col gap-1 text-sm text-black sm:min-w-48 sm:flex-1 sm:basis-0">
                    <span class="flex min-h-[2.5rem] items-end">{{ $t('requestDetailAdmin.internalOperation.managementStartDate') }}</span>
                    <input type="date" v-model="detail.management_start_date" @change="markLiquidationDirty" class="w-full min-w-0 max-w-full border border-gray-300 rounded-md px-3 py-2 text-[#7CADB9]" />
                  </label>

                  <label class="flex min-w-0 flex-col gap-1 text-sm text-black sm:min-w-48 sm:flex-1 sm:basis-0">
                    <span class="flex min-h-[2.5rem] items-end">{{ $t('requestDetailAdmin.internalOperation.originCompany') }}</span>
                    <select v-model="detail.origin_company" @change="markLiquidationDirty" class="w-full min-w-0 max-w-full border border-gray-300 rounded-md px-3 py-2 text-[#7CADB9]">
                      <option value="">{{ $t('common.select') }}</option>
                      <option v-if="detail.origin_company && !companyNames.includes(detail.origin_company)" :value="detail.origin_company">{{ detail.origin_company }}</option>
                      <option v-for="name in companyNames" :key="`origin-${detail.supply_type}-${name}`" :value="name">{{ name }}</option>
                    </select>
                  </label>

                  <label class="flex min-w-0 flex-col gap-1 text-sm text-black sm:min-w-48 sm:flex-1 sm:basis-0">
                    <span class="flex min-h-[2.5rem] items-end">{{ $t('requestDetailAdmin.internalOperation.destinationCompany') }}</span>
                    <select v-model="detail.destination_company" @change="markLiquidationDirty" class="w-full min-w-0 max-w-full border border-gray-300 rounded-md px-3 py-2 text-[#7CADB9]">
                      <option value="">{{ $t('common.select') }}</option>
                      <option v-if="detail.destination_company && !companyNames.includes(detail.destination_company)" :value="detail.destination_company">{{ detail.destination_company }}</option>
                      <option v-for="name in companyNames" :key="`destination-${detail.supply_type}-${name}`" :value="name">{{ name }}</option>
                    </select>
                  </label>

                  <label class="flex min-w-0 flex-col gap-1 text-sm text-black sm:min-w-48 sm:flex-1 sm:basis-0">
                    <span class="flex min-h-[2.5rem] items-end">{{ $t('requestDetailAdmin.internalOperation.activationDate') }}</span>
                    <input type="date" v-model="detail.activation_date" @change="markLiquidationDirty" class="w-full min-w-0 max-w-full border border-gray-300 rounded-md px-3 py-2 text-[#7CADB9]" />
                  </label>

                  <label class="flex min-w-0 flex-col gap-1 text-sm text-black sm:min-w-48 sm:flex-1 sm:basis-0">
                    <span class="flex min-h-[2.5rem] items-end">{{ $t('requestDetailAdmin.internalOperation.liquidationStatus') }}</span>
                    <select v-model="detail.liquidation_status" @change="markLiquidationDirty" class="w-full min-w-0 max-w-full border border-gray-300 rounded-md px-3 py-2 text-[#7CADB9]">
                      <option value="">{{ $t('common.select') }}</option>
                      <option value="liquidate">{{ $t('requestDetailAdmin.internalOperation.liquidate') }}</option>
                      <option value="no_liquidate">{{ $t('requestDetailAdmin.internalOperation.noLiquidate') }}</option>
                    </select>
                  </label>

                  <label class="flex min-w-0 flex-col gap-1 text-sm text-black sm:min-w-48 sm:flex-1 sm:basis-0">
                    <span class="flex min-h-[2.5rem] items-end">{{ $t('requestDetailAdmin.internalOperation.hasPermanence') }}</span>
                    <select v-model="detail.has_permanence" @change="markLiquidationDirty" class="w-full min-w-0 max-w-full border border-gray-300 rounded-md px-3 py-2 text-[#7CADB9]">
                      <option value="">{{ $t('common.select') }}</option>
                      <option value="1">{{ $t('common.yes') }}</option>
                      <option value="0">{{ $t('common.no') }}</option>
                    </select>
                  </label>

                  <label v-if="detail.has_permanence === '1'" class="flex min-w-0 flex-col gap-1 text-sm text-black sm:min-w-48 sm:flex-1 sm:basis-0">
                    <span class="flex min-h-[2.5rem] items-end">{{ $t('requestDetailAdmin.internalOperation.permanenceEndDate') }}</span>
                    <input type="date" v-model="detail.permanence_end_date" @change="markLiquidationDirty" class="w-full min-w-0 max-w-full border border-gray-300 rounded-md px-3 py-2 text-[#7CADB9]" />
                  </label>

                  <label class="flex min-w-0 flex-col gap-1 text-sm text-black sm:min-w-48 sm:flex-1 sm:basis-0">
                    <span class="flex min-h-[2.5rem] items-end">{{ $t('requestDetailAdmin.internalOperation.hasMaintenance') }}</span>
                    <select v-model="detail.has_maintenance" @change="markLiquidationDirty" class="w-full min-w-0 max-w-full border border-gray-300 rounded-md px-3 py-2 text-[#7CADB9]">
                      <option value="">{{ $t('common.select') }}</option>
                      <option value="1">{{ $t('common.yes') }}</option>
                      <option value="0">{{ $t('common.no') }}</option>
                    </select>
                  </label>

                  <label v-if="detail.has_maintenance === '1'" class="flex min-w-0 flex-col gap-1 text-sm text-black sm:min-w-48 sm:flex-1 sm:basis-0">
                    <span class="flex min-h-[2.5rem] items-end">{{ $t('requestDetailAdmin.internalOperation.maintenanceEndDate') }}</span>
                    <input type="date" v-model="detail.maintenance_end_date" @change="markLiquidationDirty" class="w-full min-w-0 max-w-full border border-gray-300 rounded-md px-3 py-2 text-[#7CADB9]" />
                  </label>

                  <!-- Permanencia y mantenimiento son del suministro cuando la solicitud lo identifica (ADSAPI-0008). -->
                  <p v-if="detail.terms_scope" class="w-full basis-full text-xs text-gray-500" :data-terms-scope="detail.terms_scope">
                    {{ detail.terms_scope === 'supply'
                      ? $t('requestDetailAdmin.internalOperation.termsOnSupply', { cups: detail.terms_cups })
                      : $t('requestDetailAdmin.internalOperation.termsOnRequest') }}
                  </p>
                </div>
              </fieldset>

              <div v-if="canEditLiquidation" class="flex justify-end pt-2">
                <ButtonWhite
                  @click="saveLiquidationDetails"
                  :disabled="isSavingLiquidation"
                  class="flex items-center gap-2"
                  :title="isSavingLiquidation ? $t('requestDetailAdmin.internalOperation.saving') : $t('requestDetailAdmin.internalOperation.save')"
                />
              </div>

              <div class="border-t border-gray-200 pt-4">
                <OperationalTracking
                  :request-data="requestData"
                  :disabled="isRequestLockedForWriting"
                  @saved="handleOperationalDetailsSaved"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Observaciones, Modificaciones, Observaciones Cuenta -->
    <!-- <div class="flex flex-col gap-4">
      <div class="w-full py-4 sm:py-6 px-3 sm:px-4 rounded-xl border shadow-[0px_4px_4px_0px_rgba(0,0,0,0.25)]">
        <label for="comments" class="text-sm sm:text-base font-medium text-black">{{ $t('requestDetailAdmin.labels.observations') }}:</label>
        <textarea :value="requestData?.observations || ''"
          @input="e => requestData && (requestData.observations = e.target.value)"
          class="w-full p-2 h-[120px] sm:h-[160px] text-sm sm:text-base text-[#7CADB9] resize-none rounded-xl border shadow-[0px_4px_4px_0px_rgba(0,0,0,0.25)] mt-2"></textarea>
      </div>
    </div> -->

    <!-- Columnas adicionales (una al lado de otra en desktop) -->
    <div class="flex flex-col lg:flex-row gap-4">
      <!-- Nueva caja: DATOS DE LA SOLICITUD -->
      <!-- <div class="w-full lg:w-1/2 flex flex-col gap-2 sm:gap-4">
        <titlePrimary title="DATOS DE LA SOLICITUD" /> -->
        <!-- <div class="p-4 sm:p-6 lg:p-8 rounded-xl border shadow-[0px_4px_4px_0px_rgba(0,0,0,0.25)] grid grid-cols-1 gap-y-2"> -->
        <!-- <div class="p-4 sm:p-6 lg:p-8 rounded-xl grid grid-cols-1 gap-y-2">
          <InputPrimary type="text" label="Estado de la solicitud" :modelValue="$t(`common.requestStatus.${requestData?.status}`) || requestData?.status || '-'"
            placeholderColor="#7CADB9" textColor="#7CADB9" labelColor="#000000" />
          <InputPrimary type="text" label="Administrador" :modelValue="requestData?.manager?.name ?? '-'"
            placeholderColor="#7CADB9" textColor="#7CADB9" labelColor="#000000" />
          <InputPrimary type="text" :label="$t('requestDetailAdmin.labels.typology')" :modelValue="typeLabel"
            placeholderColor="#7CADB9" textColor="#7CADB9" labelColor="#000000" />
          <InputPrimary type="text" label="Persona asignada"
            :modelValue="requestData?.assigned_person ?? requestData?.manager?.name ?? '-'" placeholderColor="#7CADB9"
            textColor="#7CADB9" labelColor="#000000" />
        </div>
      </div> -->
    </div>

    <!-- Línea temporal y archivos -->
    <div class="w-full flex flex-col gap-2 sm:gap-5">
      <!-- LÍNEA TEMPORAL -->
      <!-- <titlePrimary :title="$t('requestDetailAdmin.titles.timeline')" class="w-full" />
      <div class="p-4 sm:p-6 lg:p-8 rounded-xl border shadow-[0px_4px_4px_0px_rgba(0,0,0,0.25)] flex flex-col items-center justify-center gap-2">
        <div class="p-2 flex items-center justify-center space-x-3 sm:space-x-4">
          <ButtonComponent @click="previousYear" :icon="ArrowRight" />
          <span class="text-base sm:text-lg text-[#385660] font-semibold">{{ currentYear }}</span>
          <ButtonComponent @click="nextYear" :icon="ArrowLeft" />
        </div>
        <div class="p-2 sm:p-4 flex flex-col justify-center items-center gap-2 w-full">
          <div class="flex gap-1 sm:gap-2 overflow-x-auto pb-2 w-full">
            <button v-for="(month, index) in months" :key="index" @click="selectMonth(index)"
              class="py-1.5 sm:py-2 px-2 sm:px-3 rounded-full text-center text-sm sm:text-base lg:text-lg text-[#385660] transition-colors flex-shrink-0"
              :class="selectedMonth === index ? 'bg-[#2AB4D9] text-white' : 'hover:bg-blue-100'">
              {{ month }}
            </button>
          </div>
          <div v-if="selectedMonth !== null" class="flex overflow-x-auto overflow-y-visible pb-8 w-full justify-start sm:justify-center">
            <div v-for="day in daysInMonth" :key="day" class="relative">
              <span
                :ref="el => { if (el && hasEventOnDay(day)) dayRefs[day] = el }"
                class="h-7 w-7 sm:h-8 sm:w-8 flex-shrink-0 flex items-center justify-center rounded-full text-xs sm:text-sm text-[#385660] transition-colors mx-0.5 sm:mx-1 cursor-pointer"
                :class="{
                  'bg-[#2AB4D9] text-white': hasEventOnDay(day),
                  'hover:bg-blue-100': !hasEventOnDay(day)
                }" @click="selectedDay = selectedDay === day ? null : day"
                @mouseenter="event => handleDayHover(day, event)"
                @mouseleave="handleDayLeave">
                {{ day }}
              </span>
            </div>
          </div>

          <Teleport to="body">
            <div v-if="showTooltip && selectedDay !== null && hasEventOnDay(selectedDay)"
              class="fixed bg-[#2AB4D9] border border-gray-500 text-white px-3 sm:px-4 py-2 rounded-2xl text-xs sm:text-sm whitespace-nowrap min-w-[150px] sm:min-w-[200px] z-[9999] shadow-xl pointer-events-none"
              :style="{ top: tooltipPosition.top + 'px', left: tooltipPosition.left + 'px', transform: 'translate(-50%, -100%) translateY(-8px)' }">
              <div class="flex flex-col items-center gap-1 sm:gap-2">
                <div v-for="event in getEventsForDay(selectedDay)" :key="event.id"
                  class="flex flex-col justify-center items-center font-medium border-b border-white/20 last:border-0 pb-1 sm:pb-2 last:pb-0">
                  <span class="text-xs sm:text-sm">{{ event.title }}</span>
                  <span class="text-xs">{{ new Date(event.created_at).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit', hour12: false }) }}</span>
                </div>
              </div>
              <div class="absolute left-1/2 transform -translate-x-1/2 top-full w-0 h-0 border-l-[6px] border-l-transparent border-r-[6px] border-r-transparent border-t-[6px] border-t-[#2AB4D9]"></div>
            </div>
          </Teleport>
        </div>
      </div> -->

      <!-- LÍNEA TEMPORAL DE ESTADOS -->
      <!-- Cabecera colapsable: el histórico de estados arranca oculto y se despliega al pulsar -->
      <button
        type="button"
        class="w-full text-left"
        :aria-expanded="showTimeline"
        @click="showTimeline = !showTimeline"
      >
        <titlePrimary :title="$t('requestDetailAdmin.titles.timeline') || 'Línea temporal de estados'" class="backoffice-title w-full cursor-pointer">
          <svg
            class="w-4 h-4 text-white transition-transform duration-300"
            :class="{ 'rotate-180': showTimeline }"
            xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" stroke-width="3.5" stroke-linecap="round" stroke-linejoin="round"
          >
            <path d="M6 9l6 6 6-6" />
          </svg>
        </titlePrimary>
      </button>
      <div class="backoffice-card">
        <div v-if="requestData" class="grid grid-cols-1 gap-4 p-4 sm:grid-cols-3 sm:gap-6 sm:p-6 lg:p-8">
          <div class="flex flex-col gap-1">
            <span class="text-xs font-medium uppercase tracking-wider text-[#42A9C2]">{{ $t('requestDetail.timelineSummary.start') }}</span>
            <span class="text-sm text-gray-500">{{ statusTimeline.startLabel }}</span>
          </div>
          <div class="flex flex-col gap-1">
            <span class="text-xs font-medium uppercase tracking-wider text-[#42A9C2]">{{ $t('requestDetail.timelineSummary.end') }}</span>
            <span class="text-sm text-gray-500">{{ statusTimeline.endLabel }}</span>
          </div>
          <div class="flex flex-col gap-1">
            <span class="text-xs font-medium uppercase tracking-wider text-[#42A9C2]">{{ $t('requestDetail.timelineSummary.total') }}</span>
            <span class="text-sm text-gray-500">{{ statusTimeline.totalDurationLabel }}</span>
          </div>
        </div>
        <div v-if="showTimeline" class="flex flex-col gap-4 border-t border-gray-200 p-4 sm:p-6 lg:p-8">
          <div v-if="loading" class="text-center p-4">
            {{ $t('requestDetail.loading.propertyData') }}
          </div>
          <div v-else-if="error" class="text-red-500 text-center p-4">
            {{ error }}
          </div>
          <!-- informacion -->
          <div v-else-if="requestData" class="flex flex-col gap-4">
            <!-- linea de eventos: barra horizontal con puntos espaciados uniformemente (orden ASC por fecha).
                 No es proporcional al tiempo transcurrido para evitar que se rompa con huecos largos. -->
            <div v-if="statusTimeline.events.length" class="relative w-full pt-4 pb-2">
              <div class="absolute left-0 right-0 top-1/2 h-1 bg-blue-100 rounded-full"></div>
              <div class="relative flex justify-between items-start">
                <div
                  v-for="event in statusTimeline.events"
                  :key="event.id"
                  class="relative flex flex-col items-center text-center px-1"
                  style="flex: 1 1 0;"
                >
                  <div class="text-sm font-semibold text-[#385660] mb-1 break-words">{{ event.titleLabel }}</div>
                  <div class="h-4 w-4 rounded-full border-2 border-white shadow shrink-0" :class="event.isLast ? 'bg-[#2AB4D9]' : 'bg-[#7CADB9]'"></div>
                  <div class="mt-1 text-xs text-[#7CADB9] break-words">{{ event.dateLabel }}</div>
                </div>
              </div>
            </div>

            <!-- detalles de los eventos -->
            <div v-if="statusTimeline.events.length" class="flex flex-col gap-2 max-h-[280px] overflow-y-auto pr-1">
              <div
                v-for="event in statusTimeline.events"
                :key="`row-${event.id}`"
                class="border border-gray-300 rounded-md p-3 flex flex-col gap-1"
              >
                <div class="flex flex-col gap-1 text-base sm:flex-row sm:items-center sm:justify-between">
                  <div class="text-[#385660] font-semibold">
                    {{ event.titleLabel }}
                  </div>
                  <div class="text-sm text-[#7CADB9]">
                    {{ event.dateLabel }} · {{ event.elapsedLabel }}
                  </div>
                </div>
                <div v-if="event.userName || event.reason" class="text-sm text-gray-500">
                  <span v-if="event.userName">{{ event.userName }}</span>
                  <span v-if="event.userName && event.reason"> · </span>
                  <span v-if="event.reason">{{ event.reason }}</span>
                </div>
              </div>
            </div>

            <div v-else class="text-sm text-gray-400 italic">
              No hay estados disponibles para construir la línea temporal.
            </div>
          </div>
        </div>
      </div>

      <titlePrimary :title="$t('requestDetailAdmin.titles.files')" class="backoffice-title w-full">
        <RequestFilesZipButton :requestId="id" :requestCode="requestData?.code || ''" :items="itemsFiles"
          :selection="fileSelection" />
        <ButtonWhite
          v-if="canUploadFiles && !isRequestLockedForWriting"
          @click="openDocumentUploadModal"
          class="!w-auto !px-3 !py-1 !rounded-md !shadow-sm shrink-0 self-center text-sm leading-5"
          :title="$t('requestFiles.upload')"
        />
      </titlePrimary>
      <div class="backoffice-card backoffice-files-card overflow-x-auto p-4 sm:p-6 lg:p-8">
        <TableComponent :fields="files" :fileDownload="true" :items="itemsFiles" :showOptions="true"
          :selection="fileSelection" @void-file="openVoidFileModal" />
        <div v-if="totalPagesFiles > 1" class="mt-4">
          <PaginateComponent :totalPages="totalPagesFiles" :modelValue="currentPageFiles"
            @update:modelValue="handlePageChangefiles" />
        </div>
      </div>

      <FileUploadDocumentModal
        :isOpen="showDocumentUploadModal"
        :requestId="id"
        @close="showDocumentUploadModal = false"
        @upload-success="handleDocumentUploadSuccess"
      />

      </div>

      <!-- Anular un archivo subido por error (ADSREC-0026) -->
      <div v-if="fileToVoid" class="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div class="bg-white rounded-xl shadow-lg w-full max-w-md mx-4">
          <div class="flex items-center justify-between p-6 border-b">
            <h3 class="text-lg font-semibold text-[#385660]">{{ $t('requestFiles.void.title') }}</h3>
            <button @click="closeVoidFileModal" class="text-gray-400 hover:text-gray-600 transition-colors">
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>
          </div>
          <div class="p-6 flex flex-col gap-2">
            <p class="text-gray-800 font-medium break-all">{{ fileToVoid.file_name }}</p>
            <p class="text-gray-600">{{ $t('requestFiles.void.message') }}</p>
          </div>
          <div class="p-6 border-t flex gap-2 justify-end space-x-3">
            <button @click="closeVoidFileModal" :disabled="isVoidingFile"
              class="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer">
              {{ $t('requestFiles.void.cancel') }}
            </button>
            <button @click="confirmVoidFile" :disabled="isVoidingFile"
              class="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors cursor-pointer disabled:opacity-60">
              {{ $t('requestFiles.void.confirm') }}
            </button>
          </div>
        </div>
      </div>

      <!-- Confirmation Modal -->
      <div v-if="showConfirmModal" class="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div class="bg-white rounded-xl shadow-lg w-full max-w-md mx-4">
          <!-- Header -->
          <div class="flex items-center justify-between p-6 border-b">
            <h3 class="text-lg font-semibold text-[#385660]">{{ $t('requestDetailAdmin.confirmModal.title') || '¿Cancelar solicitud?' }}</h3>
            <button @click="handleCloseConfirmModal" class="text-gray-400 hover:text-gray-600 transition-colors">
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>
          </div>

          <!-- Body -->
          <div class="p-6">
            <p class="text-gray-600">{{ $t('requestDetailAdmin.confirmModal.message') || '¿Estás seguro de que deseas cancelar esta solicitud? Esta acción no se puede deshacer.' }}</p>
          </div>

          <!-- Footer -->
          <div class="p-6 border-t flex gap-2 justify-end space-x-3">
            <button @click="handleCloseConfirmModal"
              class="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer">
              {{ $t('requestDetailAdmin.confirmModal.cancel') || 'No, volver' }}
            </button>
            <button @click="confirmCancelRequest"
              class="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors cursor-pointer ">
              {{ $t('requestDetailAdmin.confirmModal.confirm') || 'Sí, cancelar solicitud' }}
            </button>
          </div>
        </div>
      </div>

    <!-- Modal -->
    <RequestTypeModal
      :isOpen="showModal"
      :currentType="requestData?.type"
      :currentOtherAction="requestData?.other_action"
      :request="requestData"
      :saving="isUpdatingType"
      :errorKeys="typeErrorKeys"
      @close="handleCloseModal"
      @confirm="handleTypeUpdate"
    />

    <!-- Status Modal -->
    <ModalSelect :isOpen="showStatusModal" :title="$t('requestDetailAdmin.modalStatus.selectStatus')"
      :listOptions="statusOptions" v-model:modelValue="selectedStatus" @close="handleCloseStatusModal"
      @confirm="handleStatusUpdate" :preventRedirect="true" :reasonRequiredValues="['cancel']"
      :reasonLabel="$t('requestDetailAdmin.modalStatus.reasonLabel')" />

    <!-- User Search Modal -->
    <div v-if="showUserSearchModal" class="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div class="bg-white rounded-xl shadow-lg w-full max-w-md mx-4 max-h-[80vh] flex flex-col">
        <!-- Header -->
        <div class="flex items-center justify-between p-6 border-b">
          <h3 class="text-lg font-semibold text-[#385660]">{{ $t('requestDetailAdmin.modalUserSearch.title') || 'Buscar Persona Asignada' }}</h3>
          <button @click="closeUserSearchModal" class="text-gray-400 hover:text-gray-600 transition-colors">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
        </div>

        <!-- Search Input -->
        <div class="p-6 border-b">
          <div class="relative">
            <input v-model="userSearchQuery" @input="searchUsers" type="text"
              :placeholder="$t('requestDetailAdmin.modalUserSearch.searchPlaceholder') || 'Buscar por nombre...'"
              class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2AB4D9] focus:border-transparent" />
            <div v-if="isSearchingUsers" class="absolute right-3 top-1/2 transform -translate-y-1/2">
              <div class="animate-spin rounded-full h-5 w-5 border-b-2 border-[#2AB4D9]"></div>
            </div>
          </div>
        </div>

        <!-- User List -->
        <div class="flex-1 overflow-y-auto p-6">
          <div v-if="isSearchingUsers && !searchedUsers.length" class="text-center py-8 text-gray-500">
            {{ $t('requestDetailAdmin.modalUserSearch.searching') || 'Buscando...' }}
          </div>
          <div v-else-if="!isSearchingUsers && !searchedUsers.length && userSearchQuery"
            class="text-center py-8 text-gray-500">
            {{ $t('requestDetailAdmin.modalUserSearch.noResults') || 'No se encontraron resultados' }}
          </div>
          <div v-else-if="!userSearchQuery" class="text-center py-8 text-gray-500">
            {{ $t('requestDetailAdmin.modalUserSearch.startTyping') || 'Escribe para buscar usuarios' }}
          </div>
          <div v-else class="space-y-2">
            <button v-for="user in searchedUsers" :key="user.id" @click="selectUser(user)"
              class="w-full text-left p-3 rounded-lg border border-gray-200 hover:bg-gray-50 hover:border-[#2AB4D9] transition-colors">
              <div class="font-medium text-[#385660]">{{ [user.name, user.surname].filter(Boolean).join(' ') }}</div>
              <div v-if="user.email" class="text-sm text-gray-500">{{ user.email }}</div>
            </button>
          </div>
        </div>

        <!-- Footer -->
        <div class="p-6 border-t flex justify-end space-x-3">
          <button @click="closeUserSearchModal"
            class="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
            {{ $t('requestDetailAdmin.modalUserSearch.cancel') || 'Cancelar' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Manager Search Modal -->
    <div v-if="showManagerSearchModal" class="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div class="bg-white rounded-xl shadow-lg w-full max-w-md mx-4 max-h-[80vh] flex flex-col">
        <!-- Header -->
        <div class="flex items-center justify-between p-6 border-b">
          <h3 class="text-lg font-semibold text-[#385660]">{{ $t('requestDetailAdmin.modalManagerSearch.title') || 'Buscar Administrador' }}</h3>
          <button @click="closeManagerSearchModal" class="text-gray-400 hover:text-gray-600 transition-colors">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
        </div>

        <!-- Search Input -->
        <div class="p-6 border-b">
          <div class="relative">
            <input v-model="managerSearchQuery" @input="searchManagers" type="text" :disabled="!!pendingAccount"
              :placeholder="$t('requestDetailAdmin.modalManagerSearch.searchPlaceholder') || 'Buscar por nombre...'"
              class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2AB4D9] focus:border-transparent" />
            <div v-if="isSearchingManagers" class="absolute right-3 top-1/2 transform -translate-y-1/2">
              <div class="animate-spin rounded-full h-5 w-5 border-b-2 border-[#2AB4D9]"></div>
            </div>
          </div>
        </div>

        <!-- Manager List -->
        <div class="flex-1 overflow-y-auto p-6">
          <div v-if="isSearchingManagers && !searchedManagers.length" class="text-center py-8 text-gray-500">
            {{ $t('requestDetailAdmin.modalManagerSearch.searching') || 'Buscando...' }}
          </div>
          <div v-else-if="!isSearchingManagers && !searchedManagers.length && managerSearchQuery"
            class="text-center py-8 text-gray-500">
            {{ $t('requestDetailAdmin.modalManagerSearch.noResults') || 'No se encontraron resultados' }}
          </div>
          <div v-else-if="!managerSearchQuery" class="text-center py-8 text-gray-500">
            {{ $t('requestDetailAdmin.modalManagerSearch.startTyping') || 'Escribe para buscar administradores' }}
          </div>
          <div v-else-if="pendingAccount" class="space-y-3">
            <!-- Cuenta horizontal y vertical: hay que indicar el canal antes de asociar -->
            <div class="p-3 rounded-lg border border-[#2AB4D9] bg-[#2AB4D9]/5">
              <div class="font-medium text-[#385660]">{{ pendingAccount.name }}</div>
              <div class="text-sm text-gray-500">{{ accountTypeLabel(pendingAccount, '') }}</div>
            </div>
            <p class="text-sm text-[#385660]">{{ $t('requestDetailAdmin.modalManagerSearch.channelPrompt') }}</p>
            <div class="flex gap-3">
              <button type="button" :disabled="isAssigningAccount" @click="assignClientAccount(pendingAccount, 'horizontal')"
                class="flex-1 px-4 py-2 rounded-lg border border-[#2AB4D9] text-[#2AB4D9] hover:bg-[#2AB4D9]/10 transition-colors disabled:opacity-50">
                Horizontal
              </button>
              <button type="button" :disabled="isAssigningAccount" @click="assignClientAccount(pendingAccount, 'vertical')"
                class="flex-1 px-4 py-2 rounded-lg border border-[#2AB4D9] text-[#2AB4D9] hover:bg-[#2AB4D9]/10 transition-colors disabled:opacity-50">
                Vertical
              </button>
            </div>
          </div>
          <div v-else class="space-y-2">
            <button v-for="account in searchedManagers" :key="account.id" :disabled="isAssigningAccount" @click="selectManager(account)"
              class="w-full text-left p-3 rounded-lg border border-gray-200 hover:bg-gray-50 hover:border-[#2AB4D9] transition-colors disabled:opacity-50">
              <div class="font-medium text-[#385660]">{{ account.name }}</div>
              <div class="text-sm text-gray-500">
                {{ accountTypeLabel(account, '') }}<span v-if="account.email"> · {{ account.email }}</span>
              </div>
            </button>
          </div>
        </div>

        <!-- Footer -->
        <div class="p-6 border-t flex justify-end space-x-3">
          <button v-if="pendingAccount" type="button" @click="pendingAccount = null"
            class="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
            {{ $t('requestDetailAdmin.modalManagerSearch.back') }}
          </button>
          <button @click="closeManagerSearchModal"
            class="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
            {{ $t('requestDetailAdmin.modalManagerSearch.cancel') || 'Cancelar' }}
          </button>
        </div>
      </div>
    </div>

    <!-- History Modal: histórico interno de trazabilidad (control interno) -->
    <div v-if="canViewHistory && showHistoryModal" class="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div class="bg-white rounded-xl shadow-lg w-full max-w-5xl max-h-[85vh] flex flex-col">
        <!-- Header -->
        <div class="flex items-center justify-between gap-4 p-6 border-b">
          <div class="flex items-center gap-3">
            <HistoryIcon class="w-6 h-6 text-[#2AB4D9] shrink-0" />
            <div>
              <h3 class="text-lg font-semibold text-[#385660]">{{ $t('requestHistory.title') }}</h3>
              <p class="text-sm text-[#7CADB9]">{{ $t('requestHistory.subtitle') }}</p>
            </div>
          </div>
          <button @click="closeHistoryModal" class="text-gray-400 hover:text-gray-600 transition-colors">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
        </div>

        <!-- Body -->
        <div class="flex-1 overflow-y-auto p-6">
          <RequestHistory :requestId="id" />
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.backoffice-card {
  border: 1px solid #B9D3DA;
  border-radius: 0.75rem;
  background-color: #fff;
  box-shadow: none;
}

.backoffice-title {
  box-shadow: none;
}

.backoffice-chat-card :deep(.message-bubble) {
  border-color: #B9D3DA;
  border-radius: 0.75rem;
  box-shadow: none;
}

.backoffice-contract-improvement :deep(.rounded-xl.border) {
  border-color: #B9D3DA;
  box-shadow: none;
}

.backoffice-files-card :deep(tbody tr) {
  box-shadow: none;
}

.backoffice-files-card :deep(tbody td) {
  border-top: 1px solid #B9D3DA;
  border-bottom: 1px solid #B9D3DA;
}

.backoffice-files-card :deep(tbody td:first-child) {
  border-left: 1px solid #B9D3DA;
}

.backoffice-files-card :deep(tbody td:last-child) {
  border-right: 1px solid #B9D3DA;
}

.backoffice-request-header,
.backoffice-request-actions {
  margin-top: 1rem;
}

.backoffice-contract-improvement {
  margin-top: 1rem;
}
</style>


<script setup>
// import InputPrimary from "@/components/inputs/input-primary.vue";
import titlePrimary from "@/components/title/title-primary.vue";
import Notes from "@/components/common/NotesComponent.vue";
import TableComponent from "@/components/table/TableComponent.vue";
import PaginateComponent from "@/components/paginate/PaginateComponent.vue";
// import ButtonComponent from "@/components/button/button-component.vue";
// import ArrowLeft from "@/assets/svg/ArrowLeft.vue";
// import ArrowRight from "@/assets/svg/ArrowRight.vue";
import HistoryIcon from "@/assets/svg/HistoryIcon.vue";
import EditIcon from "@/assets/svg/EditIcon.vue";
import ChangeIcon from "@/assets/svg/ChangeIcon.vue";
import ButtonWhite from "@/components/button/ButtonWhite.vue";
import RequestFilesZipButton from "@/components/request/RequestFilesZipButton.vue";
import { useRowSelection } from "@/composables/useRowSelection";
import { reactive } from "vue";
import ModalSelect from "@/components/modal/selected-modal.vue";
import RequestTypeModal from "@/components/request/RequestTypeModal.vue";
import { requestTypeErrorKeys } from "@/services/requestTypeChange";
import FileUploadDocumentModal from "@/components/modal/FileUploadDocumentModal.vue";
import RequestHistory from "@/components/request/RequestHistory.vue";
import OperationalTracking from "@/components/request/OperationalTracking.vue";
import HolderDataForm from "@/components/request/HolderDataForm.vue";
import ContractImprovementBlock from "@/components/request/ContractImprovementBlock.vue";
import { supportsContractImprovement } from '@/services/contractImprovement';

import { ref, computed, onMounted, onUnmounted, watch } from "vue";
import axios from "axios";
import { useRoute, useRouter } from "vue-router";
import { toast } from 'vue3-toastify';
import { useI18n } from 'vue-i18n';
import { isAdsManager } from '@/services/userRoles';
import { accountTypeLabel, accountHasBothTypes } from '@/services/clientAccount';
import { deduplicateTimelineEvents } from '@/services/requestConversation';

const router = useRouter();

// Solicitudes relacionadas por CUPS Luz y Gas
const relatedRequests = ref([]); // solicitudes con el mismo CUPS Luz
const relatedGasRequests = ref([]); // solicitudes con el mismo CUPS Gas
const loadingRelated = ref(false);
const loadingRelatedGas = ref(false);

const normalizeCups = (value) => {
  const cups = String(value ?? '').trim();
  return ['', '.', '-', 'nd', 'n/d', 'na', 'n/a', 'no disponible'].includes(cups.toLowerCase()) ? '' : cups;
};

const cupsLightValue = computed(() => normalizeCups(requestData.value?.cups_light));
const cupsGasValue = computed(() => normalizeCups(requestData.value?.cups_gas));

const fetchRelatedRequests = async (cupsLightValue) => {
  cupsLightValue = normalizeCups(cupsLightValue);
  if (!cupsLightValue) {
    relatedRequests.value = [];
    return;
  }
  loadingRelated.value = true;
  try {
    const response = await axios.get(
      `${import.meta.env.VITE_API_URL}/request/user/search`,
      {
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        params: {
          query: cupsLightValue,
          per_page: 20,
          search_by: 'cups_light',
          cups_light: cupsLightValue
        },
      }
    );
    relatedRequests.value = response.data?.value?.data?.filter(
      req => req.id !== requestData.value?.id
    ) || [];
  } catch (error) {
    relatedRequests.value = [];
    console.error("Error buscando solicitudes relacionadas:", error);
  } finally {
    loadingRelated.value = false;
  }
};

const fetchRelatedGasRequests = async (cupsGasValue) => {
  cupsGasValue = normalizeCups(cupsGasValue);
  if (!cupsGasValue) {
    relatedGasRequests.value = [];
    return;
  }
  loadingRelatedGas.value = true;
  try {
    const response = await axios.get(
      `${import.meta.env.VITE_API_URL}/request/user/search`,
      {
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        params: {
          query: cupsGasValue,
          per_page: 20,
          search_by: 'cups_gas',
          cups_gas: cupsGasValue
        },
      }
    );
    relatedGasRequests.value = response.data?.value?.data?.filter(
      req => req.id !== requestData.value?.id
    ) || [];
  } catch (error) {
    relatedGasRequests.value = [];
    console.error("Error buscando solicitudes relacionadas por gas:", error);
  } finally {
    loadingRelatedGas.value = false;
  }
};

const route = useRoute();
const { t: $t } = useI18n();
const id = route.params.id;

const role = localStorage.getItem("role");
// El personal de ADS se reparte el trabajo: asigna y reasigna una solicitud
// aunque la gestione otra persona (ADS-0126).
const canAssignRequest = isAdsManager();

const requestData = ref(null);
const loading = ref(true);
const error = ref(null);
const pollingInterval = ref(null);
const isFirstLoad = ref(true);

const setRequestData = (data) => {
  if (!data) {
    requestData.value = null;
    return;
  }

  if (data.id !== requestData.value?.id || data.can_edit !== true || data.can_view_operational_details !== true) {
    isLiquidationDirty.value = false;
  }

  const normalizedSupplies = Array.isArray(data.supplies)
    ? data.supplies.map((supply) => ({
      ...supply,
      company: supply?.company ?? data?.company ?? null,
    }))
    : [];

  requestData.value = {
    ...data,
    supplies: normalizedSupplies,
  };

  if (!isLiquidationDirty.value) {
    liquidationDetails.value = buildLiquidationDetails(data, normalizedSupplies);
  }

};

// const currentYear = ref(new Date().getFullYear());
// const selectedMonth = ref(new Date().getMonth());
// const selectedDay = ref(null);
// const tooltipPosition = ref({ top: 0, left: 0 });
// const showTooltip = ref(false);

const itemsFiles = ref([]);
// Casillas para descargar en un ZIP solo los archivos marcados (ADSREC-0025).
const fileSelection = reactive(useRowSelection(computed(() => itemsFiles.value.length)));
const currentPageFiles = ref(1);
const totalPagesFiles = ref(1);

const showModal = ref(false);
const showStatusModal = ref(false);
const showDocumentUploadModal = ref(false);
const showHistoryModal = ref(false);
const selectedStatus = ref('');
const isUpdatingType = ref(false);
// Motivo por el que el back rechazó el cambio de tipología, para mostrarlo en el modal.
const typeErrorKeys = ref([]);

// Manager search modal variables
const showManagerSearchModal = ref(false);
const managerSearchQuery = ref('');
const searchedManagers = ref([]);
const isSearchingManagers = ref(false);
const managerSearchTimeout = ref(null);
// Cuenta horizontal y vertical a la espera de que el gestor indique el canal.
const pendingAccount = ref(null);
const isAssigningAccount = ref(false);

// User search modal variables
const showUserSearchModal = ref(false);
const userSearchQuery = ref('');
const searchedUsers = ref([]);
const isSearchingUsers = ref(false);
const userSearchTimeout = ref(null);

const statusOptions = computed(() => [
  { key: 'created', label: $t('common.requestStatus.created') },
  { key: 'review', label: $t('common.requestStatus.review') },
  { key: 'pending', label: $t('common.requestStatus.pending') },
  { key: 'results', label: $t('common.requestStatus.results') },
  { key: 'cancel', label: $t('common.requestStatus.cancel') },
]);

const showConfirmModal = ref(false);
const showTimeline = ref(false); // línea temporal oculta por defecto
const isUpdatingStatus = ref(false);
const lastUpdateTime = ref(null);

// const months = ref([
//   "Enero",
//   "Febrero",
//   "Marzo",
//   "Abril",
//   "Mayo",
//   "Junio",
//   "Julio",
//   "Agosto",
//   "Septiembre",
//   "Octubre",
//   "Noviembre",
//   "Diciembre",
// ]);

// const dayRefs = ref({});

// const handleDayHover = (day, event) => {
//   if (!hasEventOnDay(day)) return;

//   selectedDay.value = day;
//   showTooltip.value = true;

//   const rect = event.target.getBoundingClientRect();
//   tooltipPosition.value = {
//     top: rect.top,
//     left: rect.left + rect.width / 2
//   };
// };

// const handleDayLeave = () => {
//   selectedDay.value = null;
//   showTooltip.value = false;
// };

const files = computed(() => [
  { key: "date", label: $t('requestFiles.columns.date') },
  { key: "documental_type", label: $t('requestFiles.columns.documentalType') },
  { key: "name", label: $t('requestFiles.columns.originalName') },
  { key: "user", label: $t('requestFiles.columns.uploader') },
  { key: "opciones", label: $t('requestFiles.columns.options') }
]);

// Computed properties
// const daysInMonth = computed(() => {
//   if (selectedMonth.value === null) return 0;
//   return new Date(currentYear.value, selectedMonth.value + 1, 0).getDate();
// });

const clientAccountName = computed(() => (
  requestData.value?.client_account?.name
  || requestData.value?.manager?.name
  || '-'
));

// Tipo de cuenta del cliente (horizontal, vertical o ambos), resuelto por el
// back en `client_account.type_label` con los nombres de la base de datos.
const clientAccountTypeLabel = computed(() => accountTypeLabel(requestData.value?.client_account, '-'));

const requestSupplyAddress = computed(() => (
  requestData.value?.supply_address
  || requestData.value?.supplies?.find((supply) => supply?.address)?.address
  || requestData.value?.property?.address
  || requestData.value?.property?.name
  || requestData.value?.user_address
  || '-'
));

const typeLabel = computed(() => {
  const type = requestData.value?.type;
  if (!type) return '-';

  const baseLabel = $t(`common.requestTypes.${type}`) || type;

  if (type === 'other_type') {
    const actionKey = requestData.value?.other_action;
    if (!actionKey) return baseLabel || '-';

    // Try multiple translation paths for the action
    const candidateKeys = [
      `form.otherType.actions.${actionKey}`,
      `form.otherType.${actionKey}`,
      `common.requestTypes.${actionKey}`,
      `common.otherActions.${actionKey}`
    ];

    let actionLabel = '';
    for (const k of candidateKeys) {
      const tVal = $t(k);
      if (tVal && tVal !== k) {
        actionLabel = tVal;
        break;
      }
    }

    // Fallback: humanize the key
    if (!actionLabel) {
      actionLabel = actionKey.replace(/_/g, ' ');
      actionLabel = actionLabel.charAt(0).toUpperCase() + actionLabel.slice(1);
    }

    return (baseLabel || $t('common.requestTypes.other_type') || 'Otro tipo') + (actionLabel ? ' - ' + actionLabel : '');
  }

  return baseLabel || '-';
});

const formatFileUploadDate = (value) => {
  if (!value) return '-';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '-';

  return parsed.toLocaleString('es-ES', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
};

const getFileUploaderName = (file) => {
  return file.user || file.uploader?.username || file.uploader?.name || '-';
};

// Visible fields according to request type
const visible = computed(() => {
  const type = requestData.value?.type;
  const v = {
    cups: true,
    name: false,
    email: false,
    dni: false,
    telephone: false,
    iban: false,
    supply_address: false,
    user_address: false,
    postal: false,
    municipality: false,
    // Lectura de contador: se pide solo en cambio de titularidad y de
    // comercializadora (SOL-05). En el resto de tipos el bloque aparece
    // únicamente si hay un valor heredado.
    meter_readings: false,
    file: true,
  };

  switch (type) {
    case 'supplies': // Alta de suministro
      v.name = v.dni = v.email = v.telephone = v.supply_address = v.user_address = v.postal = v.municipality = true;
      v.iban = true;
      break;
    case 'change_ownership': // Titular
      v.name = v.dni = v.email = v.telephone = v.supply_address = v.user_address = v.postal = v.municipality = true;
      v.iban = true;
      v.meter_readings = true;
      break;
    // Retirada «Cambio de comercializadora» (ADS-0130): se deja comentada, no borrada, por si hubiera que reponerla.
    // case 'change_supplier': // Comercializadora (mismo titular)
    //   v.name = v.dni = v.email = v.telephone = v.supply_address = v.user_address = v.postal = v.municipality = true;
    //   v.iban = true;
    //   v.meter_readings = true;
    //   break;
    case 'account_change': // Cambio de cuenta
      v.cups = true;
      v.iban = true;
      v.supply_address = v.postal = v.municipality = true;
      v.telephone = true;
      v.name = v.email = v.dni = false;
      break;
    case 'invoice': // Factura
      v.cups = true;
      v.supply_address = v.postal = v.municipality = v.file = v.telephone = true;
      // La dirección de correspondencia se pide en factura, igual que en alta y cambio de titular
      v.user_address = true;
      v.name = v.email = v.dni = v.iban = false;
      break;
    case 'other_type': // Otro tipo
      v.cups = true;
      v.telephone = v.supply_address = v.postal = v.municipality = v.file = true;
      v.email = requestData.value?.other_action === 'cambio_direccion_postal' || !!requestData.value?.email;
      v.name = v.dni = v.iban = v.user_address = false;
      break;
    default:
      v.name = v.email = v.dni = v.telephone = v.supply_address = v.postal = v.municipality = true;
  }

  return v;
});

const canUploadFiles = computed(() => {
  return Boolean(requestData.value);
});

// El backend indica si el usuario es el gestor responsable o un perfil
// expresamente autorizado para intervenir. El resto solo puede consultarla.
const canEditRequest = computed(() => requestData.value?.can_edit === true);
const canEditLiquidation = computed(() => requestData.value?.can_view_operational_details === true && !isRequestLockedForWriting.value);

const isAssignedToAnotherManager = computed(
  () => Boolean(requestData.value?.assigned_to) && !canEditRequest.value
);

const isUnassignedRequest = computed(
  () => Boolean(requestData.value) && !requestData.value?.assigned_to
);

// El histórico interno solo lo ven los perfiles responsables autorizados.
const canViewHistory = computed(() => requestData.value?.can_view_history === true);

const isRequestLockedForWriting = computed(() => {
  const status = (requestData.value?.status || '').toString().toLowerCase();
  return ['cancel', 'results'].includes(status) || !canEditRequest.value;
});

// Methods
const handleClickChangeStatus = () => {
  if (!canEditRequest.value) return;
  showStatusModal.value = true;
};

const handleCloseStatusModal = () => {
  showStatusModal.value = false;
  selectedStatus.value = '';
};

const handleClickChange = () => {
  if (!canEditRequest.value) return;
  showModal.value = true;
};

const handleCloseModal = () => {
  showModal.value = false;
  typeErrorKeys.value = [];
};

const handleTypeUpdate = async (payload) => {
  if (!payload?.type || isUpdatingType.value) return;
  // Si mientras el modal estaba abierto la solicitud cambió de responsable,
  // se explica en lugar de no hacer nada al pulsar «Guardar cambio».
  if (!canEditRequest.value) {
    typeErrorKeys.value = [isUnassignedRequest.value
      ? 'requestDetail.locked.unassigned'
      : 'requestDetail.locked.assignedToAnotherManager'];
    return;
  }
  isUpdatingType.value = true;
  typeErrorKeys.value = [];

  try {
    const response = await axios.put(
      `${import.meta.env.VITE_API_URL}/request/update/${id}`,
      payload,
      {
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      }
    );

    if (response.data?.value) {
      await fetchrequestDetailAdmin();
      showModal.value = false;
      toast.success($t('requestDetailAdmin.typology.updated'));
    } else {
      typeErrorKeys.value = ['requestTypeChange.errors.generic'];
    }
  } catch (error) {
    console.error("Error updating request type:", error);
    typeErrorKeys.value = requestTypeErrorKeys(error);
  } finally {
    isUpdatingType.value = false;
  }
};

const handleStatusUpdate = async (value, reason) => {
  if (!canEditRequest.value || !value || isUpdatingStatus.value) return;

  // Optimistic UX: close modal immediately
  isUpdatingStatus.value = true;
  showStatusModal.value = false;
  selectedStatus.value = '';
  console.log('Iniciando actualización de estado:', value);

  try {
    const response = await axios.put(
      `${import.meta.env.VITE_API_URL}/request/update/${id}`,
      { status: value, reason: reason || null },
      {
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      }
    );

    if (response.data?.value) {
      console.log('Respuesta de actualización:', response.data.value);
      setRequestData(response.data.value);
      lastUpdateTime.value = new Date().getTime();

      toast.success("Estado de la solicitud actualizado con éxito", {
        autoClose: 500,
        position: "top-right",
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: false,
        draggable: true,
      });

      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } else {
      requestData.value = null;
      error.value = "No se encontraron datos para esta Comunidades.";
      toast.error('No se pudo actualizar el estado');
    }
  } catch (err) {
    console.error("Error updating request status:", err);
    toast.error('Error al actualizar el estado');
  } finally {
    isUpdatingStatus.value = false;
  }
};

// Internal operational data. It is only returned by the API to ADS managers.
const companies = ref([]);
const companyNames = computed(() => {
  const source = Array.isArray(companies.value) ? companies.value : [];
  const names = source
    .map((company) => String(company?.name ?? company ?? '').trim())
    .filter(Boolean);
  return [...new Set(names)];
});

const liquidationDetails = ref([]);
const isSavingLiquidation = ref(false);
const isLiquidationDirty = ref(false);

const markLiquidationDirty = () => {
  if (!canEditLiquidation.value || isSavingLiquidation.value) return;
  isLiquidationDirty.value = true;
};

const normalizeLegacyLiquidationStatus = (status) => {
  const normalized = String(status ?? '').trim().toLowerCase();
  if (['liquidate', 'liquidado', '1', 'true', 'si', 'sí'].includes(normalized)) return 'liquidate';
  if (['no_liquidate', 'no_liquidado', 'decomisionado', '0', 'false', 'no'].includes(normalized)) return 'no_liquidate';
  return '';
};

const buildLiquidationDetails = (data, normalizedSupplies = []) => {
  const storedDetails = Array.isArray(data?.liquidation_details) ? data.liquidation_details : [];

  return ['light', 'gas'].map((supplyType) => {
    const stored = storedDetails.find((detail) => detail.supply_type === supplyType);
    const supply = normalizedSupplies.find((item) => {
      const type = String(item?.type ?? '').toLowerCase();
      return supplyType === 'light' ? ['light', 'luz'].includes(type) : type === 'gas';
    });

    // Permanencia y mantenimiento llegan aparte, del suministro si la
    // solicitud lo identifica (ADSAPI-0008); si no, de la propia solicitud.
    const terms = data?.supply_terms?.[supplyType] ?? stored;

    return {
      supply_type: supplyType,
      management_start_date: stored?.management_start_date ?? '',
      origin_company: stored ? (stored.origin_company ?? '') : (supply?.company ?? ''),
      destination_company: stored ? (stored.destination_company ?? '') : (data?.company ?? ''),
      liquidation_status: stored ? (stored.liquidation_status ?? '') : normalizeLegacyLiquidationStatus(data?.liquidate),
      activation_date: stored?.activation_date ?? '',
      has_permanence: terms?.has_permanence === true ? '1' : terms?.has_permanence === false ? '0' : '',
      permanence_end_date: terms?.permanence_end_date ?? '',
      has_maintenance: terms?.has_maintenance === true ? '1' : terms?.has_maintenance === false ? '0' : '',
      maintenance_end_date: terms?.maintenance_end_date ?? '',
      terms_scope: data?.supply_terms?.[supplyType]?.scope ?? null,
      terms_cups: data?.supply_terms?.[supplyType]?.cups ?? null,
    };
  });
};

const fetchCompanies = async () => {
  try {
    const response = await axios.get(`${import.meta.env.VITE_API_URL}/company`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
    });
    if (Array.isArray(response.data)) {
      companies.value = response.data;
    } else if (Array.isArray(response.data?.value)) {
      companies.value = response.data.value;
    } else {
      companies.value = [];
    }
  } catch (err) {
    console.error('Error fetching companies:', err);
    companies.value = [];
  }
};

const saveLiquidationDetails = async () => {
  if (!canEditLiquidation.value || isSavingLiquidation.value) return;
  const id = requestData.value.id;
  isSavingLiquidation.value = true;

  // `terms_scope` y `terms_cups` solo sirven para mostrar dónde se guarda el dato.
  const payloadDetails = liquidationDetails.value.map((detail) => ({
    supply_type: detail.supply_type,
    management_start_date: detail.management_start_date,
    origin_company: detail.origin_company,
    destination_company: detail.destination_company,
    liquidation_status: detail.liquidation_status,
    activation_date: detail.activation_date,
    has_permanence: detail.has_permanence === '' ? null : detail.has_permanence === '1',
    permanence_end_date: detail.has_permanence === '1' && detail.permanence_end_date ? detail.permanence_end_date : null,
    has_maintenance: detail.has_maintenance === '' ? null : detail.has_maintenance === '1',
    maintenance_end_date: detail.has_maintenance === '1' && detail.maintenance_end_date ? detail.maintenance_end_date : null,
  }));

  try {
    const response = await axios.put(
      `${import.meta.env.VITE_API_URL}/request/${id}/liquidation`,
      { details: payloadDetails },
      {
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      }
    );
    if (Array.isArray(response.data?.value)) {
      requestData.value.liquidation_details = response.data.value;
      requestData.value.supply_terms = response.data.supply_terms ?? requestData.value.supply_terms;
      liquidationDetails.value = buildLiquidationDetails(requestData.value, requestData.value.supplies);
      isLiquidationDirty.value = false;
      toast.success($t('requestDetailAdmin.internalOperation.saved'));
    } else {
      toast.error($t('requestDetailAdmin.internalOperation.saveError'));
    }
  } catch (err) {
    console.error('Error updating liquidation details:', err);
    toast.error($t('requestDetailAdmin.internalOperation.saveError'));
  } finally {
    isSavingLiquidation.value = false;
  }
};

const handleOperationalDetailsSaved = (details) => {
  if (!requestData.value) return;
  requestData.value.registration_classification = details.registration_classification ?? '';
  requestData.value.pending_reason = details.pending_reason ?? '';
};

// Los datos del titular se guardan desde la propia sección; se refleja el
// resultado en la solicitud cargada para que el resto de la vista cuadre.
const handleHolderDataSaved = (holder) => {
  if (!requestData.value) return;
  Object.assign(requestData.value, holder);
};

const handleClickEdit = () => {
  if (!canEditRequest.value) return;

  // Mapeo de tipos a rutas
  const typeToRoute = {
    supplies: '/forms/supplies',
    change_ownership: '/forms/change_ownership',
    // Retirada «Cambio de comercializadora» (ADS-0130): se deja comentada, no borrada, por si hubiera que reponerla.
    // change_supplier: '/forms/change_supplier',
    account_change: '/forms/account_change',
    invoice: '/forms/invoice',
    other_type: '/forms/other_type',
  };

  const route = typeToRoute[requestData.value.type];

  if (route) {
    // Preparar los datos para el formulario
    const formDataToEdit = {
      id: requestData.value.id,
      code: requestData.value.code,
      type: requestData.value.type,
      status: requestData.value.status,
      cups_gas: requestData.value.cups_gas,
      cups_light: requestData.value.cups_light,
      assigned_to: requestData.value.assigned_to?.id || 0,
      manager: requestData.value.manager?.id || 0,
      // La cuenta del cliente y el canal se decidieron al crear la solicitud:
      // viajan a la edición para no perderlos al guardar.
      client_account_id: requestData.value.client_account?.id ?? requestData.value.client_account_id ?? null,
      request_role: requestData.value.request_role ?? '',
      observations: requestData.value.observations,
      user_address: requestData.value.user_address,
      supply_address: requestData.value.supply_address,
      postal_code: requestData.value.postal_code,
      telephone: requestData.value.telephone,
      // «No dispongo» del teléfono del titular: al modificar se respeta lo indicado al crear.
      telephone_unavailable: requestData.value.telephone_unavailable === true,
      email: requestData.value.email,
      contact_name: requestData.value.contact_name,
      contact_surname: requestData.value.contact_surname,
      contact_person: requestData.value.contact_person,
      light_meter_reading: requestData.value.light_meter_reading,
      gas_meter_reading: requestData.value.gas_meter_reading,
      dni_nie: requestData.value.dni_nie,
      iban: requestData.value.iban,
      supplies: Array.isArray(requestData.value.supplies)
        ? requestData.value.supplies.map((supply) => ({
          ...supply,
          company: supply?.company ?? requestData.value?.company ?? null,
        }))
        : [],
      files: requestData.value.files || [],
      isEditing: true,
      description: requestData.value.description,
      other_action: requestData.value.other_action,
      // Consentimientos originales, para mostrarlos marcados y bloqueados en el
      // formulario de modificación (ADS-0127).
      accepted_terms_at: requestData.value.accepted_terms_at,
      accepted_data_processing_at: requestData.value.accepted_data_processing_at,
      accepted_commercial_comms_at: requestData.value.accepted_commercial_comms_at,
      includes_water: requestData.value.includes_water ?? false
    };

    // Guardar los datos en localStorage
    localStorage.setItem('editRequestData', JSON.stringify(formDataToEdit));

    console.log('Redirigiendo a:', route, 'con datos:', formDataToEdit);
    router.push({
      path: route,
      query: { edit: '1' }
    });
  } else {
    console.log('Tipo de solicitud no reconocido:', requestData.value.type);
  }
};

// Manager search modal functions
const openManagerSearchModal = () => {
  showManagerSearchModal.value = true;
  managerSearchQuery.value = '';
  searchedManagers.value = [];
  pendingAccount.value = null;
};

const closeManagerSearchModal = () => {
  showManagerSearchModal.value = false;
  managerSearchQuery.value = '';
  searchedManagers.value = [];
  pendingAccount.value = null;
  isSearchingManagers.value = false;
  if (managerSearchTimeout.value) {
    clearTimeout(managerSearchTimeout.value);
    managerSearchTimeout.value = null;
  }
};

const searchManagers = async () => {
  if (!managerSearchQuery.value.trim()) {
    searchedManagers.value = [];
    return;
  }

  // Clear previous timeout
  if (managerSearchTimeout.value) {
    clearTimeout(managerSearchTimeout.value);
  }

  // Set loading state
  isSearchingManagers.value = true;

  // Debounce search
  managerSearchTimeout.value = setTimeout(async () => {
    try {
      // El cliente de una solicitud es una cuenta (administrador de fincas,
      // API, inmobiliaria), que es lo que la gestora conoce. Se busca en
      // `client_accounts` por nombre y email, igual que la columna
      // Administrador del listado, y no entre las personas de contacto.
      const response = await axios.get(
        `${import.meta.env.VITE_API_URL}/client-account/search`,
        {
          params: {
            query: managerSearchQuery.value.trim(),
            per_page: 20
          },
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      const accountsPage = response.data?.value;
      searchedManagers.value = Array.isArray(accountsPage?.data) ? accountsPage.data : [];
    } catch (error) {
      console.error("Error searching client accounts:", error);
      searchedManagers.value = [];
      toast.error("Error al buscar clientes");
    } finally {
      isSearchingManagers.value = false;
    }
  }, 300); // 300ms debounce
};

const selectManager = async (account) => {
  // Una cuenta horizontal y vertical no permite deducir el canal: se pide
  // antes de asociar. Antes este caso acababa en "Error al actualizar el
  // administrador" porque el back lo rechazaba con 422.
  if (accountHasBothTypes(account)) {
    pendingAccount.value = account;
    return;
  }
  await assignClientAccount(account, null);
};

const assignClientAccount = async (account, channel) => {
  if (isAssigningAccount.value) return;
  isAssigningAccount.value = true;
  try {
    const payload = { client_account_id: account.id };
    if (channel) payload.request_role = channel;

    const response = await axios.put(
      `${import.meta.env.VITE_API_URL}/request/update/${id}`,
      payload,
      {
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      }
    );

    if (response.data?.value) {
      setRequestData(response.data.value);
      toast.success("Cliente de la solicitud actualizado");
      closeManagerSearchModal();

      // Llamar al endpoint request/get para refrescar los datos
      await fetchrequestDetailAdmin();
    } else {
      toast.error("No se pudo actualizar el cliente de la solicitud");
    }
  } catch (error) {
    console.error("Error updating client account:", error);
    const serverMessage = error.response?.data?.value;
    toast.error(typeof serverMessage === 'string' ? serverMessage : "Error al actualizar el cliente de la solicitud");
  } finally {
    isAssigningAccount.value = false;
  }
};

// User search modal functions
const openUserSearchModal = () => {
  showUserSearchModal.value = true;
  userSearchQuery.value = '';
  searchedUsers.value = [];
};

const closeUserSearchModal = () => {
  showUserSearchModal.value = false;
  userSearchQuery.value = '';
  searchedUsers.value = [];
  isSearchingUsers.value = false;
  if (userSearchTimeout.value) {
    clearTimeout(userSearchTimeout.value);
    userSearchTimeout.value = null;
  }
};

const searchUsers = async () => {
  if (!userSearchQuery.value.trim()) {
    searchedUsers.value = [];
    return;
  }

  // Clear previous timeout
  if (userSearchTimeout.value) {
    clearTimeout(userSearchTimeout.value);
  }

  // Set loading state
  isSearchingUsers.value = true;

  // Debounce search
  userSearchTimeout.value = setTimeout(async () => {
    try {
      const response = await axios.get(
        `${import.meta.env.VITE_API_URL}/user/search`,
        {
          params: {
            query: userSearchQuery.value.trim(),
            search_by: 'name',
            status: 'active',
            role: 'superadmin,admin'
          },
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      const usersPage = response.data?.value;
      searchedUsers.value = Array.isArray(usersPage?.data) ? usersPage.data : [];
    } catch (error) {
      console.error("Error searching users:", error);
      searchedUsers.value = [];
      toast.error("Error al buscar usuarios");
    } finally {
      isSearchingUsers.value = false;
    }
  }, 300); // 300ms debounce
};

const selectUser = async (user) => {
  try {
    const response = await axios.put(
      `${import.meta.env.VITE_API_URL}/request/update/${id}`,
      { assigned_to: user.id },
      {
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      }
    );

    if (response.data?.value) {
      setRequestData(response.data.value);
      toast.success("Persona asignada actualizada con éxito");
      closeUserSearchModal();

      // Llamar al endpoint request/get para refrescar los datos
      await fetchrequestDetailAdmin();
    } else {
      toast.error("No se pudo actualizar la persona asignada");
    }
  } catch (error) {
    console.error("Error updating assigned user:", error);
    toast.error("Error al actualizar la persona asignada");
  }
};

const handleCloseConfirmModal = () => {
  showConfirmModal.value = false;
};

const confirmCancelRequest = async () => {
  if (isUpdatingStatus.value) return;
  // Close modal immediately for friendlier UX
  isUpdatingStatus.value = true;
  showConfirmModal.value = false;
  console.log('Cancelando solicitud...');

  try {
    const response = await axios.put(
      `${import.meta.env.VITE_API_URL}/request/update/${id}`,
      { status: 'cancel' },
      {
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      }
    );

    if (response.data?.value) {
      console.log('Respuesta de actualización:', response.data.value);
      setRequestData(response.data.value);
      lastUpdateTime.value = new Date().getTime();

      // Mostrar el toast con duración más corta
      toast.success("Solicitud cancelada con éxito", {
        autoClose: 500,
        position: "top-right",
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: false,
        draggable: true,
      });

      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } else {
      requestData.value = null;
      error.value = "No se encontraron datos para esta solicitud.";
      toast.error("Error al cancelar la solicitud");
    }
  } catch (err) {
    console.error("Error al cancelar la solicitud:", err);
    toast.error("Error al cancelar la solicitud");
  } finally {
    isUpdatingStatus.value = false;
  }
};

// const previousYear = () => {
//   currentYear.value--;
// };

// const nextYear = () => {
//   currentYear.value++;
// };

// const selectMonth = (monthIndex) => {
//   selectedMonth.value = monthIndex;
//   // Reset selected day when changing month
//   selectedDay.value = null;
// };

const fetchrequestDetailAdmin = async () => {
  if (!id) {
    error.value = "No ID provided in route.";
    loading.value = false;
    return;
  }

  // Si acabamos de actualizar, no hacemos el fetch
  if (lastUpdateTime.value && (new Date().getTime() - lastUpdateTime.value) < 5000) {
    return;
  }

  if (isFirstLoad.value) {
    loading.value = true;
  }
  error.value = null;
  try {
    const response = await axios.get(
      `${import.meta.env.VITE_API_URL}/request/get/${id}`,
      {
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      }
    );
    if (response.data?.value) {
      setRequestData(response.data.value);
      // Update files data from the request response
      if (response.data.value.files) {
        itemsFiles.value = response.data.value.files.map(file => ({
          id: file.id,
          date: formatFileUploadDate(file.created_at),
          documental_type: file.documental_type || '-',
          name: file.file_name,
          user: getFileUploaderName(file),
          file_path: file.file_path,
          file_name: file.file_name,
          is_available: file.is_available !== false,
          can_void: file.can_void === true,
          preview_url: `${import.meta.env.VITE_API_URL}/request/file/${file.id}/download?disposition=inline`,
          download_url: `${import.meta.env.VITE_API_URL}/request/file/${file.id}/download`
        }));
      }
    } else {
      requestData.value = null;
      if (isFirstLoad.value) {
        error.value = "No se encontraron datos para esta Comunidades.";
      }
    }
  } catch (err) {
    console.error("Error fetching Comunidades detail:", err);
    if (isFirstLoad.value) {
      error.value = "Failed to load Comunidades details.";
    }
    requestData.value = null;
  } finally {
    if (isFirstLoad.value) {
      loading.value = false;
    }
    isFirstLoad.value = false;
  }
};

const handlePageChangefiles = (newPage) => {
  currentPageFiles.value = newPage;
  // No need to fetch files data since it comes with the request
};

// const hasEventOnDay = (day) => {
//   if (!requestData.value?.events?.length) return false;
//   return requestData.value.events.some(event => {
//     const eventDate = new Date(event.created_at);
//     return eventDate.getDate() === day &&
//       eventDate.getMonth() === selectedMonth.value &&
//       eventDate.getFullYear() === currentYear.value;
//   });
// };

// const getEventsForDay = (day) => {
//   if (!requestData.value?.events?.length) return [];
//   return requestData.value.events.filter(event => {
//     const eventDate = new Date(event.created_at);
//     return eventDate.getDate() === day &&
//       eventDate.getMonth() === selectedMonth.value &&
//       eventDate.getFullYear() === currentYear.value;
//   });
// };

const parseValidDate = (value) => {
  if (!value) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const formatTimelineDate = (value) => {
  const parsed = parseValidDate(value);
  if (!parsed) return '-';
  return parsed.toLocaleString('es-ES', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  });
};

const formatDuration = (milliseconds) => {
  if (!Number.isFinite(milliseconds) || milliseconds <= 0) return '0 min';

  const minutes = Math.floor(milliseconds / (1000 * 60));
  if (minutes < 60) return `${Math.max(1, minutes)} min`;

  const hours = milliseconds / (1000 * 60 * 60);
  if (hours < 24) return `${hours.toFixed(hours >= 10 ? 0 : 1)} h`;

  const days = milliseconds / (1000 * 60 * 60 * 24);
  return `${days.toFixed(days >= 10 ? 0 : 1)} días`;
};

const statusTimeline = computed(() => {
  if (!requestData.value) {
    return {
      events: [],
      startLabel: '-',
      endLabel: '-',
      totalDurationLabel: '-'
    };
  }

  const baseCreatedAt = parseValidDate(requestData.value.created_at);
  const rawEvents = deduplicateTimelineEvents(
    Array.isArray(requestData.value.events) ? requestData.value.events : []
  );

  const normalizedEvents = rawEvents
    .map((event) => {
      const eventDate = parseValidDate(event.created_at);
      if (!eventDate) return null;
      return {
        id: event.id,
        title: event.title || 'status',
        created_at: event.created_at,
        date: eventDate,
        userName: [event.user?.name, event.user?.surname].filter(Boolean).join(' ') || null,
        reason: event.reason || null
      };
    })
    .filter(Boolean)
    .sort((firstEvent, secondEvent) => firstEvent.date.getTime() - secondEvent.date.getTime());

  if (baseCreatedAt) {
    const hasCreatedEvent = normalizedEvents.some((event) => event.title === 'created');
    if (!hasCreatedEvent) {
      normalizedEvents.unshift({
        id: 'request-created',
        title: 'created',
        created_at: requestData.value.created_at,
        date: baseCreatedAt,
        userName: null,
        reason: null
      });
    }
  }

  // Se muestran TODOS los estados en su fecha real, incluido el cierre ('results' = Cerrada,
  // 'cancel' = Cancelada). Antes se ocultaba 'results' y se reetiquetaba 'pending' como
  // 'results'; con el histórico real migrado eso escondía el cierre y lo situaba en la fecha
  // equivocada, así que se muestra cada estado tal cual.
  // Orden ASC por fecha; una línea temporal proporcional al tiempo se rompía visualmente
  // cuando había huecos largos entre eventos, así que ahora se muestra como lista ordenada.
  const visibleEvents = normalizedEvents;

  if (!visibleEvents.length) {
    return {
      events: [],
      startLabel: '-',
      endLabel: '-',
      totalDurationLabel: '-'
    };
  }
  const startDate = baseCreatedAt || visibleEvents[0].date;
  const endDate = visibleEvents[visibleEvents.length - 1].date;
  const totalMilliseconds = Math.max(0, endDate.getTime() - startDate.getTime());

  const events = visibleEvents.map((event, index) => {
    const elapsed = Math.max(0, event.date.getTime() - startDate.getTime());
    const displayKey = event.title;
    return {
      id: event.id,
      titleLabel: $t(`common.requestStatus.${displayKey}`) || displayKey,
      dateLabel: formatTimelineDate(event.created_at),
      elapsedLabel: totalMilliseconds > 0 ? formatDuration(elapsed) : '0 min',
      userName: event.userName,
      reason: event.reason,
      isLast: index === visibleEvents.length - 1
    };
  });

  return {
    events,
    startLabel: formatTimelineDate(startDate),
    endLabel: formatTimelineDate(endDate),
    totalDurationLabel: formatDuration(totalMilliseconds)
  };
});

const openDocumentUploadModal = () => {
  if (!canUploadFiles.value || isRequestLockedForWriting.value) {
    return;
  }

  showDocumentUploadModal.value = true;
};

// El histórico interno solo se abre si el backend confirma que el perfil está autorizado.
const openHistoryModal = () => {
  if (!canViewHistory.value) return;
  showHistoryModal.value = true;
};

const closeHistoryModal = () => {
  showHistoryModal.value = false;
};

const handleDocumentUploadSuccess = async () => {
  await fetchrequestDetailAdmin();
};

// Anular un archivo subido por error (ADSREC-0026): el archivo deja de verse y
// la anulación queda en el histórico interno.
const fileToVoid = ref(null);
const isVoidingFile = ref(false);

const openVoidFileModal = (file) => {
  fileToVoid.value = file;
};

const closeVoidFileModal = () => {
  if (isVoidingFile.value) return;
  fileToVoid.value = null;
};

const confirmVoidFile = async () => {
  if (!fileToVoid.value || isVoidingFile.value) return;
  const fileId = fileToVoid.value.id;
  isVoidingFile.value = true;
  try {
    await axios.post(
      `${import.meta.env.VITE_API_URL}/request/file/${fileId}/void`,
      {},
      {
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      }
    );
    itemsFiles.value = itemsFiles.value.filter((file) => file.id !== fileId);
    if (Array.isArray(requestData.value?.files)) {
      requestData.value.files = requestData.value.files.filter((file) => file.id !== fileId);
    }
    toast.success($t('requestFiles.void.success'));
  } catch (err) {
    console.error('Error voiding request file:', err);
    toast.error($t('requestFiles.void.error'));
  } finally {
    isVoidingFile.value = false;
    fileToVoid.value = null;
  }
};

const startPolling = () => {
  // Clear any existing interval
  if (pollingInterval.value) {
    clearInterval(pollingInterval.value);
  }

  // Set up new interval to fetch data every 15 seconds
  pollingInterval.value = setInterval(() => {
    fetchrequestDetailAdmin();
  }, 15000);
};

const stopPolling = () => {
  if (pollingInterval.value) {
    clearInterval(pollingInterval.value);
    pollingInterval.value = null;
  }
};


const navigateToRequest = (requestId) => {
  if (requestId) {
    const href = router.resolve({ name: 'request-detail', params: { id: String(requestId) } }).href;
    window.open(href, '_blank', 'noopener,noreferrer');
  }
};

onMounted(() => {
  fetchrequestDetailAdmin();
  fetchCompanies();
  startPolling();
  // Buscar solicitudes relacionadas al cargar los datos principales
  // Se ejecuta tras la primera carga de datos
  const unwatch = watch(requestData, (val) => {
    fetchRelatedRequests(val?.cups_light);
    fetchRelatedGasRequests(val?.cups_gas);
  }, { immediate: true });
  onUnmounted(() => unwatch());
});

// Clean up interval when component is unmounted
onUnmounted(() => {
  stopPolling();
});
</script>
